import React, { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Linkedin, Plus } from "lucide-react";

const COLUMNS = [
  {
    title: "The report",
    links: [
      { label: "The shift", href: "#overview" },
      { label: "Three failures", href: "#problem" },
      { label: "How they coped", href: "#workaround" },
      { label: "Out of reach", href: "#blocked" },
    ],
  },
  {
    title: "The solution",
    links: [
      { label: "Building moved", href: "#builders" },
      { label: "Running on it", href: "#operations" },
      { label: "The payoff", href: "#unlocked" },
    ],
  },
  {
    title: "The businesses",
    links: [
      { label: "Operator stories", href: "#stories" },
      { label: "What we observed", href: "#observed" },
      { label: "Methodology", href: "#methodology" },
    ],
  },
  {
    title: "Emergent",
    links: [
      { label: "emergent.sh", href: "https://emergent.sh", external: true },
      { label: "Case studies", href: "https://emergent.sh/case-studies", external: true },
      { label: "News", href: "https://emergent.sh/news", external: true },
      { label: "Blog", href: "https://emergent.sh/blog", external: true },
    ],
  },
];

const XLogo = ({ size = 19 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

function Socials() {
  return (
    <div className="flex items-center gap-5">
      <a href="https://www.instagram.com/emergentlabs/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[#0A0A0A] hover:opacity-60 transition-opacity" data-testid="footer-instagram">
        <Instagram size={20} strokeWidth={1.7} />
      </a>
      <a href="https://x.com/emergentlabs" target="_blank" rel="noopener noreferrer" aria-label="X" className="text-[#0A0A0A] hover:opacity-60 transition-opacity" data-testid="footer-x">
        <XLogo />
      </a>
      <a href="https://www.linkedin.com/company/emergentlabs" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-[#0A0A0A] hover:opacity-60 transition-opacity" data-testid="footer-linkedin">
        <Linkedin size={20} strokeWidth={1.7} />
      </a>
    </div>
  );
}

function FooterLink({ l }) {
  return (
    <a
      href={l.href}
      {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="block text-[15px] leading-[1.65] text-[#3D3D3B] hover:text-[#0A0A0A] transition-colors"
    >
      {l.label}
    </a>
  );
}

function MobileAccordion() {
  const [open, setOpen] = useState(null);
  return (
    <div className="md:hidden" data-testid="footer-accordion">
      {COLUMNS.map((c, i) => (
        <div key={c.title} className="border-t border-dotted border-[#B9B9B4]">
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between py-4 text-left"
            data-testid={`footer-accordion-${i}`}
            aria-expanded={open === i}
          >
            <span className="text-[16px] text-[#0A0A0A]">{c.title}</span>
            <Plus size={20} className={`text-[#0A0A0A] transition-transform duration-300 ${open === i ? "rotate-45" : ""}`} />
          </button>
          <motion.div
            initial={false}
            animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-5 space-y-2">
              {c.links.map((l) => <FooterLink key={l.label} l={l} />)}
            </div>
          </motion.div>
        </div>
      ))}
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="bg-[#F7F7F5] text-[#0A0A0A]" data-testid="footer">
      <div className="border-t border-[#DDDDD8]" />

      {/* ── Desktop ── */}
      <div className="hidden md:block">
        {/* giant wordmark */}
        <div className="px-10 lg:px-16 py-16 lg:py-20 flex justify-center" data-testid="footer-wordmark">
          <motion.img
            src="/brand/emergent-logo-ink.svg"
            alt="Emergent"
            className="w-full max-w-[1560px] h-auto select-none"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>

        {/* link columns */}
        <div className="border-t border-[#DDDDD8]">
          <div className="grid grid-cols-4 gap-8 px-10 lg:px-16 py-14 lg:py-16">
            {COLUMNS.map((c) => (
              <div key={c.title}>
                <p className="font-mono text-[14.5px] tracking-[0.08em] text-[#0A0A0A]">{c.title}</p>
                <div className="mt-7 space-y-1.5">
                  {c.links.map((l) => <FooterLink key={l.label} l={l} />)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-[#DDDDD8] px-8 py-4 flex items-center justify-between">
          <p className="font-mono text-[12px] tracking-[0.14em] uppercase text-[#0A0A0A]">Copyright Emergent 2026</p>
          <Socials />
        </div>
      </div>

      {/* ── Mobile ── */}
      <div className="md:hidden px-5 pt-4">
        <div className="py-10 flex justify-center">
          <img src="/brand/emergent-logo-ink.svg" alt="Emergent" className="w-full h-auto select-none" />
        </div>
        <MobileAccordion />
        <div className="border-t border-dotted border-[#B9B9B4] py-6 flex flex-col items-center gap-4">
          <Socials />
          <p className="font-mono text-[12px] tracking-[0.14em] uppercase text-[#0A0A0A] text-center">Copyright Emergent 2026</p>
        </div>
      </div>
    </footer>
  );
}
